import { getAppId } from '@/constants/app';
import {
    APP_BRANDING_READY_EVENT,
    createFallbackAppBranding,
    type IAppBranding,
    type TAppBrandingSource,
} from '@/constants/app-branding';
import { useAppBrandingStore } from '@/store/app-branding/slice';

const SOURCE_RANK: Record<TAppBrandingSource, number> = {
    pending: 0,
    fallback: 1,
    cache: 2,
    network: 3,
};

function shouldApplyBranding(incoming: IAppBranding, current: IAppBranding): boolean {
    if (incoming.appId !== getAppId()) {
        return false;
    }
    if (incoming.appId !== current.appId && current.appId) {
        return true;
    }
    return SOURCE_RANK[incoming.source] >= SOURCE_RANK[current.source];
}

function readBrandingFromWindow(): IAppBranding | null {
    if (typeof window === 'undefined') {
        return null;
    }
    return window.__H5R_APP_BRANDING__ ?? null;
}

/** document.title 以 branding.name 为准（覆盖 Next metadata 水合回写） */
export function syncDocumentTitleFromBranding(branding: IAppBranding): void {
    if (typeof document === 'undefined' || !branding.name) {
        return;
    }
    document.title = branding.name;
}

/** 将 head script / fallback 写入的全局状态同步到 Zustand（幂等，按 source 优先级合并） */
export function hydrateAppBrandingStore(explicit?: IAppBranding): void {
    const incoming = explicit ?? readBrandingFromWindow();
    if (!incoming) {
        return;
    }

    const current = useAppBrandingStore.getState().branding;
    if (shouldApplyBranding(incoming, current)) {
        useAppBrandingStore.getState().hydrate(incoming);
        syncDocumentTitleFromBranding(incoming);
        return;
    }

    // store 未更新时仍同步 title，避免 Next metadata 水合把 early script 的品牌名盖掉
    syncDocumentTitleFromBranding(current);
}

/** 协议页 gate 超时或接口失败后的降级品牌 */
export function applyFallbackAppBranding(): void {
    const fallback = createFallbackAppBranding(getAppId());
    if (typeof window !== 'undefined') {
        window.__H5R_APP_BRANDING__ = fallback;
        window.dispatchEvent(new CustomEvent(APP_BRANDING_READY_EVENT, { detail: fallback }));
    }
    hydrateAppBrandingStore(fallback);
}

export { APP_BRANDING_READY_EVENT };
